import { Loader2 } from 'lucide-react'
import * as React from 'react'
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'

import { apiUrl } from '@/lib/api'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ScatterLeaderboardEntry {
  name: string
  domain: string
  favicon_url: string
  mention_rate: number
  sentiment_score: number
  avg_position: number | null
  scan_count: number
  is_own_brand: boolean
  is_brand_placeholder: boolean
  prompt_coverage?: number
}

interface PlotPoint {
  x: number
  y: number
  name: string
  favicon_url: string
  avg_position: number | null
  is_own_brand: boolean
}

// ── Chart margins ──────────────────────────────────────────────────────────────

const MARGIN = { top: 24, right: 28, bottom: 44, left: 52 }

// ── Custom Dot ─────────────────────────────────────────────────────────────────

function FaviconDot(props: Record<string, unknown>) {
  const { cx, cy, payload } = props as { cx: number; cy: number; payload: PlotPoint }
  const uid = React.useId().replace(/:/g, '')
  const size = 28
  const half = size / 2
  const rx = 5
  const isOwn = payload.is_own_brand
  const initial = (payload.name?.[0] ?? '?').toUpperCase()
  const clipId  = `sc-clip-${uid}`
  const gradId  = `sc-grad-${uid}`
  const bGradId = `sc-bgrad-${uid}`

  return (
    <g style={{ cursor: 'pointer' }}>
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
        {isOwn && (
          <linearGradient id={bGradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        )}
        <clipPath id={clipId}>
          <rect x={cx - half} y={cy - half} width={size} height={size} rx={rx} ry={rx} />
        </clipPath>
      </defs>

      {isOwn && (
        <>
          <rect x={cx - half - 10} y={cy - half - 10} width={size + 20} height={size + 20} rx={rx + 6} ry={rx + 6} fill="rgba(99,102,241,0.10)" />
          <rect x={cx - half - 5}  y={cy - half - 5}  width={size + 10} height={size + 10} rx={rx + 3} ry={rx + 3} fill="rgba(99,102,241,0.18)" />
        </>
      )}

      <rect x={cx - half} y={cy - half} width={size} height={size} rx={rx} ry={rx} fill={`url(#${gradId})`} />
      <text x={cx} y={cy + 0.5} textAnchor="middle" dominantBaseline="middle" fontSize={14} fontWeight={700} fill="rgba(255,255,255,0.95)" style={{ pointerEvents: 'none' }}>
        {initial}
      </text>

      {payload.favicon_url && (
        <image href={payload.favicon_url} x={cx - half} y={cy - half} width={size} height={size} clipPath={`url(#${clipId})`} preserveAspectRatio="xMidYMid slice" />
      )}

      {isOwn && (
        <>
          <rect x={cx - half - 2} y={cy - half - 2} width={size + 4} height={size + 4} rx={rx + 1} ry={rx + 1} fill="none" stroke={`url(#${bGradId})`} strokeWidth={2.5} />
          <text x={cx} y={cy + half + 11} textAnchor="middle" dominantBaseline="middle" fontSize={8} fontWeight={700} letterSpacing={1} fill="rgba(99,102,241,0.85)" style={{ pointerEvents: 'none' }}>
            YOU
          </text>
        </>
      )}
    </g>
  )
}

// ── Custom Tooltip ─────────────────────────────────────────────────────────────

function ScatterTooltip({ active, payload }: { active?: boolean | undefined; payload?: Array<{ payload: PlotPoint }> | undefined }) {
  if (!active || !payload?.length) return null
  const d = payload[0]!.payload

  const sentColor = d.y >= 70 ? '#16a34a' : d.y >= 40 ? '#ca8a04' : '#dc2626'
  const posColor =
    d.avg_position === null ? null
    : d.avg_position <= 3 ? '#16a34a'
    : d.avg_position <= 6 ? '#ca8a04'
    : '#64748b'

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-lg" style={{ minWidth: 200, pointerEvents: 'none' }}>
      <div className="flex items-center gap-2 border-b border-border px-3.5 py-2.5">
        {d.favicon_url && (
          <img src={d.favicon_url} width={20} height={20} alt={d.name} className="shrink-0 rounded-[5px] object-contain" style={{ background: '#f1f5f9' }} />
        )}
        <span className="flex-1 truncate text-sm font-bold text-text">{d.name}</span>
        {d.is_own_brand && (
          <span className="shrink-0 rounded-full bg-brand-accent/10 px-2 py-0.5 text-[9px] font-bold text-brand-accent">you</span>
        )}
      </div>
      <div className="space-y-0 px-3.5 py-2">
        {[
          { label: 'Visibility', value: `${d.x}%`, color: '#6366f1' },
          { label: 'Sentiment', value: String(d.y), color: sentColor },
          ...(d.avg_position !== null ? [{ label: 'Avg Rank', value: `#${d.avg_position}`, color: posColor ?? '#64748b' }] : []),
        ].map(({ label, value, color }, i, arr) => (
          <div key={label} className={`flex items-center justify-between gap-5 py-1.5 ${i < arr.length - 1 ? 'border-b border-border' : ''}`}>
            <span className="text-xs text-text-muted">{label}</span>
            <span className="text-xs font-semibold" style={{ color }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Quadrant corner labels ─────────────────────────────────────────────────────

const QUADRANT_CORNERS = [
  { label: 'AI LEADERS',  style: { right:  'calc(28px + 12px)', top:    'calc(24px + 12px)', textAlign: 'right' as const } },
  { label: 'HIDDEN GEMS', style: { left:   'calc(52px + 12px)', top:    'calc(24px + 12px)' } },
  { label: 'POLARISING',  style: { right:  'calc(28px + 12px)', bottom: 'calc(44px + 12px)', textAlign: 'right' as const } },
  { label: 'OFF RADAR',   style: { left:   'calc(52px + 12px)', bottom: 'calc(44px + 12px)' } },
] as const

// ── Main Component ─────────────────────────────────────────────────────────────

interface CompetitorScatterPlotProps {
  leaderboard: ScatterLeaderboardEntry[]
  loading?: boolean
  compact?: boolean
}

export function CompetitorScatterPlot({ leaderboard, loading = false, compact = false }: CompetitorScatterPlotProps) {
  const entries = leaderboard.filter((e) => e.scan_count > 0)
  const source = compact ? entries.slice(0, 8) : entries

  const plotData: PlotPoint[] = source.map((e) => ({
    x: e.prompt_coverage ?? e.mention_rate,
    y: e.sentiment_score,
    name: e.name,
    favicon_url: e.favicon_url ? apiUrl(e.favicon_url) : '',
    avg_position: e.avg_position,
    is_own_brand: e.is_own_brand,
  }))

  const height = compact ? 280 : 480
  const headerPad = compact ? '16px 20px 12px' : '20px 24px 14px'
  const chartPad  = compact ? '8px 4px 0'       : '12px 8px 0'

  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="border-b border-border px-6 py-5">
          <div className="mb-1 h-4 w-44 animate-pulse rounded bg-surface-overlay" />
          <div className="h-3 w-56 animate-pulse rounded bg-surface-overlay" />
        </div>
        <div className="flex items-center justify-center" style={{ height }}>
          <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
        </div>
      </div>
    )
  }

  if (plotData.length < 2) {
    return (
      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <div style={{ padding: headerPad }} className="border-b border-border">
          <h3 className="text-sm font-semibold text-text">Competitive Landscape</h3>
          <p className="mt-0.5 text-xs text-text-muted">Visibility vs sentiment</p>
        </div>
        <div className="flex flex-col items-center justify-center gap-1" style={{ height }}>
          <p className="text-xs text-text-muted">Not enough data to plot</p>
          <p className="text-[11px] text-text-muted opacity-60">Run prompts to build visibility data</p>
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      <div style={{ padding: headerPad }} className="border-b border-border">
        <h3 className="text-sm font-semibold text-text">Competitive Landscape</h3>
        <p className="mt-0.5 text-xs text-text-muted">
          {compact
            ? 'Visibility vs sentiment · top 8 brands'
            : `Visibility vs sentiment · ${plotData.length} brands`}
        </p>
      </div>

      <div style={{ padding: chartPad, position: 'relative' }}>
        <div style={{ position: 'absolute', inset: chartPad, pointerEvents: 'none', zIndex: 1 }}>
          {QUADRANT_CORNERS.map(({ label, style }) => (
            <span
              key={label}
              style={{
                position: 'absolute',
                ...style,
                fontSize: 8,
                fontWeight: 700,
                letterSpacing: '0.15em',
                color: 'rgba(15,23,42,0.35)',
                whiteSpace: 'nowrap',
                userSelect: 'none',
              }}
            >
              {label}
            </span>
          ))}
        </div>

        <ResponsiveContainer width="100%" height={height}>
          <ScatterChart margin={MARGIN}>
            <ReferenceLine x={50} stroke="rgba(15,23,42,0.12)" strokeWidth={1} strokeDasharray="4 3" />
            <ReferenceLine y={50} stroke="rgba(15,23,42,0.12)" strokeWidth={1} strokeDasharray="4 3" />
            <XAxis
              type="number" dataKey="x"
              domain={[0, 100]} tickCount={6}
              tick={{ fill: '#64748b', fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
              tickFormatter={(v: number) => `${v}%`}
              label={{ value: 'Visibility %', position: 'insideBottom', offset: -16, fill: '#64748b', fontSize: 10, letterSpacing: 0.5 }}
            />
            <YAxis
              type="number" dataKey="y"
              domain={[0, 100]} tickCount={6}
              tick={{ fill: '#64748b', fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
              label={{ value: 'Sentiment', angle: -90, position: 'insideLeft', offset: 16, fill: '#64748b', fontSize: 10, letterSpacing: 0.5 }}
            />
            <Tooltip
              content={(props) => {
                const p = props as unknown as { active?: boolean; payload?: Array<{ payload: PlotPoint }> }
                return <ScatterTooltip active={p.active} payload={p.payload} />
              }}
              cursor={false}
            />
            <Scatter
              data={plotData}
              shape={(shapeProps: unknown) => <FaviconDot {...(shapeProps as Record<string, unknown>)} />}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
